import { useTranslation } from "react-i18next";

/**
 * The line that says a text was written by a language model and may be
 * wrong — on every answer the model gives, asked for by the owner on
 * 2026-09-28 after reading an area report that put no grand cru in the Vallée
 * de la Marne.
 *
 * **One component, so the wording is one string.** Five places show what the
 * model wrote — the window help, the reasons kept on a wine, *Till maten*, the
 * quality report and an area report — and each already had a sentence of its
 * own about what the answer is for. Those stay: they say what the answer does
 * and does not touch. This says something none of them did, the same way
 * everywhere, so a reader learns it once.
 *
 * **Above the answer, not under it**, so it is read before the text it
 * qualifies rather than after it has been believed.
 *
 * **Red, on the owner's word the same evening** — the first version was the
 * muted ink with *AI* in a grey outline, and was asked to say it more clearly.
 * It is the one place outside the drinking-window band that uses the band's
 * red, which `design-direction.md` otherwise keeps for *past*: a warning that
 * the text may be wrong is the nearest thing this interface has to that
 * meaning, and a second red would be a second meaning. Not colour alone: the
 * mark is a filled disc with an exclamation mark, the word *AI-genererat*
 * leads the sentence, and the box has an edge in the same red, so it reads
 * the same to somebody who cannot tell red from green.
 */
export function AiNotice({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <p
      role="note"
      className={`border-past/60 bg-past/5 text-ink flex max-w-prose items-start gap-2.5 rounded-sm border px-3 py-2 text-sm ${className}`}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0">
        <circle cx="8" cy="8" r="8" className="fill-past" />
        <path
          d="M8 4v5"
          className="stroke-surface"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="8" cy="11.8" r="1" className="fill-surface" />
      </svg>
      <span>
        <strong className="text-past font-semibold">{t("ai.mark")}</strong>{" "}
        {t("ai.notice")}
      </span>
    </p>
  );
}
